import { useState } from "react";
import { documentService } from "@miplatform/api";
import { useComponentState, useDebounce } from "@miplatform/hooks";
import {
  IApiRequest,
  ICreateDocumentCategoryRequest,
  IDocumentCategoriesResponse,
} from "@miplatform/types";

export const useDocumentCategories = () => {
  const [search, setSearch] = useState("");
  const [documentCategories, setDocumentCategories] = useState<
    IDocumentCategoriesResponse[]
  >([]);
  const [recentDocumentCategories, setRecentDocumentCategories] = useState<
    IDocumentCategoriesResponse[]
  >([]);

  const stateManager = useComponentState();
  const debouncedSearch = useDebounce(search);

  const getDocumentCategories = async ({
    top,
    skip,
    filters,
  }: IApiRequest = {}) => {
    const combinedFilters = {
      ...filters,
      name: { contains: debouncedSearch },
    };

    return stateManager.run(async () => {
      return documentService.documentCategories.getAll({
        top,
        skip,
        sort: "name",
        filters: combinedFilters,
      });
    });
  };

  const getDocumentCategoriesByType = async (type: string) => {
    return stateManager.run(async () => {
      return documentService.documentCategories.getByDependentType({
        entityType: type,
      });
    });
  };

  const createDocumentCategory = async ({
    data,
  }: {
    data: ICreateDocumentCategoryRequest;
  }) => {
    return stateManager.run(async () => {
      return documentService.documentCategories.create({
        data,
      });
    });
  };

  const updateDocumentCategory = async ({
    data,
  }: {
    data: IDocumentCategoriesResponse;
  }) => {
    return stateManager.run(async () => {
      return documentService.documentCategories.update({
        data,
      });
    });
  };

  const deleteDocumentCategory = async ({
    id,
    modifiedOn,
  }: {
    id: string;
    modifiedOn: string;
  }) => {
    return stateManager.run(async () => {
      return documentService.documentCategories.delete({ id, modifiedOn });
    });
  };

  return {
    documentCategories,
    setDocumentCategories,
    recentDocumentCategories,
    setRecentDocumentCategories,
    getDocumentCategories,
    search,
    setSearch,
    debouncedSearch,
    deleteDocumentCategory,
    createDocumentCategory,
    updateDocumentCategory,
    isLoading: stateManager.isBusy(),
    getDocumentCategoriesByType,
  };
};
